import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, AutocompleteInputChangeReason, TextField } from '@mui/material';
import { DEBOUNCE_TIMEOUT_MS } from '../../../utils/constants';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import { ErrorAlert } from '../ErrorAlert';

interface Props<T> {
  multiple?: boolean;
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  options?: T[];
  loading: boolean;
  loadingError: boolean;
  value?: T[] | T;
  loadOptions?: (search: string | undefined, cancelToken: CancelToken) => void;
  getOptionId: (option: T) => string;
  getOptionName: (option: T) => string;
  getValueIfNotFound?: (option: T) => T;
  onChange: (value?: T[] | T | null) => void;
}

export function QcsAutocompleteWithoutFormik<T>({
  multiple,
  label,
  disabled,
  required,
  options,
  loading,
  loadingError,
  value,
  loadOptions,
  getOptionId,
  getOptionName,
  getValueIfNotFound,
  onChange,
}: Props<T>): ReactElement {
  const [initializing, setInitializing] = useState(true);
  const [open, setOpen] = useState(false);
  const dataCancelToken = useRef<CancelTokenSource | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInitializing(false);

    return () => {
      if (timeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(timeoutRef.current);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      dataCancelToken.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (_event: any, value: T | T[] | null) => {
    onChange(value);
  };

  const handleInputChange = (_event: any, value: string, reason: AutocompleteInputChangeReason) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    dataCancelToken.current?.cancel();

    if (reason === 'reset') {
      return;
    }

    if (reason === 'clear') {
      if (!open) {
        return;
      }

      dataCancelToken.current = axios.CancelToken.source();
      loadOptions?.(value || undefined, dataCancelToken.current.token);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      dataCancelToken.current?.cancel();
      dataCancelToken.current = axios.CancelToken.source();

      loadOptions?.(value || undefined, dataCancelToken.current.token);
    }, DEBOUNCE_TIMEOUT_MS);
  };

  const handleOpen = (event: React.ChangeEvent<any>) => {
    setOpen(true);
    const value = event?.target.value;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    dataCancelToken.current?.cancel();
    dataCancelToken.current = axios.CancelToken.source();

    loadOptions?.(value || undefined, dataCancelToken.current.token);
  };

  const handleClose = () => {
    setOpen(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    dataCancelToken.current?.cancel();
  };

  const filteredOptions = useMemo(() => {
    if (loadingError) {
      return [];
    }

    if (multiple) {
      return [...(options ?? []), ...(value as T[]).filter((x) => !options?.map((y) => getOptionId(y)).includes(getOptionId(x)))];
    }

    if (value === undefined || value === null || options?.map((x) => getOptionId(x)).includes(getOptionId(value as T))) {
      return options ?? [];
    }

    return [...(options ?? []), getValueIfNotFound?.(value as T) ?? (value as T)];
  }, [multiple, getOptionId, options, value, getValueIfNotFound, loadingError]);

  return (
    <Autocomplete
      multiple={multiple}
      sx={{
        backgroundColor: (theme) => theme.palette.common.white,
      }}
      options={filteredOptions}
      isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          //Dont use required parameter.
          //Toggle only the asterisk element: swapping label text nodes crashes React (removeChild) once a translator extension has rewritten them.
          label={label ? <>{label}{required && <span> *</span>}</> : undefined}
          slotProps={{ htmlInput: params.inputProps }}
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.common.white,
            },
          })}
        />
      )}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: T) => (
        <li {...props} key={getOptionId(option)}>
          {getOptionName(option)}
        </li>
      )}
      getOptionLabel={(option: T) => getOptionName(option)}
      loading={initializing || loading}
      noOptionsText={loadingError ? <ErrorAlert /> : undefined}
      value={value === undefined ? null : value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={disabled}
    />
  );
}
